import * as v from 'valibot';

// LOB Category enum based on trip types and international/domestic flow
export const LobCategoryEnum = v.union([
  v.literal('intl_acabs'), // International Airport Cabs
  v.literal('intl_ocabs'), // International Outstation Cabs (OW/RT)
  v.literal('intl_hrcabs'), // International Hourly Rental Cabs
  v.literal('dom_acabs'), // Domestic Airport Cabs
  v.literal('dom_ocabs'), // Domestic Outstation Cabs (OW/RT)
  v.literal('dom_hrcabs') // Domestic Hourly Rental Cabs₹
]);

// Page Context Schema
export const PageContextCabs = v.object({
  lob: v.string(),
  lob_category: v.optional(v.nullable(LobCategoryEnum)),
  sub_lob: v.optional(v.nullable(v.string())),
  page_name: v.optional(v.nullable(v.string())),
  prev_page_name: v.optional(v.nullable(v.string())),
  subpage_name: v.optional(v.nullable(v.string())),
  funnel_step: v.optional(v.nullable(v.string())),
  pg_time_interval: v.optional(v.nullable(v.number())),
  navigation: v.optional(v.nullable(v.string())),
  page_type: v.string(),
  page_url: v.optional(v.nullable(v.string())),
  page_title: v.optional(v.nullable(v.string()))
});

// Event Tracking Context Schema
export const EventTrackingContextCabs = v.object({
  template_id: v.number(),
  topic_name: v.string(),
  env: v.string(),
  timezone: v.string(),
  session_id: v.string(),
  request_id: v.optional(v.nullable(v.string())),
  journey_id: v.optional(v.nullable(v.string())),
  bu: v.optional(v.nullable(v.string())),
  organisation: v.optional(v.nullable(v.string())),
  funnel_source: v.string(),
  funnel_entry: v.optional(v.nullable(v.string())),
  traffic_source: v.optional(v.nullable(v.string())),
  domain_country: v.optional(v.nullable(v.string())),
  domain_currency: v.optional(v.nullable(v.string())),
  domain_language: v.optional(v.nullable(v.string())),
  affiliate_id: v.optional(v.nullable(v.string())),
  meta_req_id_ls: v.optional(
    v.nullable(v.array(v.optional(v.nullable(v.string()))))
  ),
  producer: v.optional(v.nullable(v.string())),
  region: v.optional(v.nullable(v.string())),
  user_preferred_currency: v.optional(v.nullable(v.string())),
  rate_of_exchange: v.optional(v.nullable(v.number()))
});

// Error Details Schema
export const ErrorDetailsItemCabs = v.object({
  code: v.string(),
  message: v.string(),
  severity: v.optional(v.nullable(v.string())),
  source: v.optional(v.nullable(v.string()))
});

// User Context Schema
export const UserContextCabs = v.object({
  uuid: v.optional(v.nullable(v.string())),
  is_logged_in: v.boolean(),
  marketing_cloud_id: v.optional(v.nullable(v.string())),
  login_channel: v.optional(v.nullable(v.string())),
  profile_type: v.optional(v.nullable(v.string())),
  wallet_bal: v.optional(v.nullable(v.number())),
  login_type: v.optional(v.nullable(v.string())),
  loyalty_status: v.optional(v.nullable(v.string())),
  loyalty_type: v.optional(v.nullable(v.string())),
  loyalty_level: v.optional(v.nullable(v.string())),
  user_segment_list: v.optional(v.nullable(v.array(v.string()))),
  user_type: v.optional(v.nullable(v.string())),
  agency_code: v.optional(v.nullable(v.string()))
});

// Device Context Schema
export const DeviceContextCabs = v.object({
  device_id: v.string(),
  ip_addr: v.optional(v.nullable(v.string())),
  network_type: v.optional(v.nullable(v.string())),
  os_version: v.string(),
  user_agent: v.optional(v.nullable(v.string())),
  time_zone: v.string(),
  platform: v.string(),
  country: v.optional(v.nullable(v.string())),
  region: v.optional(v.nullable(v.string())),
  city: v.optional(v.nullable(v.string())),
  lat: v.optional(v.nullable(v.number())),
  long: v.optional(v.nullable(v.number())),
  app_ver: v.string(),
  build_ver: v.string(),
  os: v.string(),
  visitor_id: v.optional(v.nullable(v.string())),
  advertiser_id: v.optional(v.nullable(v.string())),
  vendor_id: v.optional(v.nullable(v.string())),
  device_token: v.optional(v.nullable(v.string())),
  model: v.optional(v.nullable(v.string())),
  carrier: v.optional(v.nullable(v.string()))
});

// Experiment Details Schema
export const ExperimentDetailsCabs = v.object({
  valid_exp_list: v.array(v.string()),
  variant_keys: v.string(),
  honoured_exp_list: v.array(v.string())
});

// Position Schema
export const PositionCabs = v.object({
  v: v.number(),
  h: v.optional(v.nullable(v.number()))
});

// Price Item Schema for Products
export const PriceItemCabs = v.object({
  base_fare: v.number(),
  toll_fare: v.number(),
  service_fare: v.number(),
  extra_km_fare: v.number(),
  extra_time_fare: v.number(),
  discounts: v.optional(
    v.nullable(
      v.object({
        applicable_coupons: v.array(
          v.object({
            code: v.string(),
            discount: v.number(),
            is_applied: v.boolean()
          })
        )
      })
    )
  )
});

// Product List Item Schema
export const ProductListItemCabs = v.object({
  id: v.optional(v.nullable(v.string())),
  name: v.optional(v.nullable(v.string())),
  category: v.optional(v.nullable(v.string())),
  rank: v.optional(v.nullable(v.number())),
  position: v.optional(v.nullable(PositionCabs)),
  price: v.optional(v.nullable(PriceItemCabs)),
  rating: v.optional(v.nullable(v.string())),
  sitting_capacity: v.optional(v.nullable(v.string())),
  luggage_capacity: v.optional(v.nullable(v.string())),
  supplier_name: v.optional(v.nullable(v.string())),
  fuel_type: v.optional(v.nullable(v.string())),
  tags: v.optional(v.nullable(v.array(v.optional(v.nullable(v.string()))))),
  free_cancellation_tag: v.optional(v.nullable(v.string())),
  model_name: v.optional(v.nullable(v.string())),
  category_id: v.optional(v.nullable(v.string()))
});

// Pokus Key Schema
export const PokusKeyCabs = v.object({
  pokus_key_name: v.string(),
  pokus_key_value: v.string()
});

// Filter Range Schema
export const FilterRangeCabs = v.object({
  start: v.number(),
  end: v.number()
});

// Filter Applied Item Schema
export const FilterAppliedItemCabs = v.object({
  range: v.optional(v.nullable(FilterRangeCabs)),
  group: v.string(),
  range_filter: v.boolean(),
  category: v.optional(v.nullable(v.string())),
  value: v.string(),
  source: v.optional(v.nullable(v.string()))
});

// Filters Schema
export const FiltersCabs = v.object({
  applied: v.optional(v.nullable(v.array(FilterAppliedItemCabs))),
  removed: v.optional(v.nullable(v.array(FilterAppliedItemCabs))),
  seen: v.optional(
    v.nullable(
      v.array(
        v.object({
          range: v.string(),
          group: v.string(),
          range_filter: v.string(),
          category: v.string(),
          value: v.string(),
          source: v.string()
        })
      )
    )
  ),
  clear_all_clicked: v.optional(v.nullable(v.boolean()))
});

// Content Details Item Schema
export const ContentDetailsItemCabs = v.object({
  id: v.string(),
  type: v.string(),
  name: v.optional(v.nullable(v.string())),
  category_id: v.optional(v.nullable(v.string())),
  category: v.optional(v.nullable(v.string())),
  position: PositionCabs
});

// Components Schema
export const ComponentsCabs = v.object({
  id: v.string(),
  filters: v.optional(v.nullable(FiltersCabs)),
  content_details: v.optional(v.nullable(v.array(ContentDetailsItemCabs))),
  time_spent: v.optional(v.nullable(v.number())),
  product_list: v.optional(v.nullable(v.array(ProductListItemCabs))),
  avg_pricing: v.optional(v.nullable(v.number())),
  is_premium_user: v.optional(v.nullable(v.boolean())),
  pokus_keys: v.optional(v.nullable(v.array(PokusKeyCabs))),
  cabs_specific_trip_type: v.optional(v.nullable(v.string())),
  airport_transfer_type: v.optional(v.nullable(v.string())),
  cabs_trip_duration: v.optional(v.nullable(v.number())),
  flight_number: v.optional(v.nullable(v.string())),
  search_id: v.optional(v.nullable(v.string())),
  cab_id: v.optional(v.nullable(v.string())),
  package_id: v.optional(v.nullable(v.string()))
});

// Booking Info Schema
export const BookingInfoCabs = v.object({
  currency: v.string(),
  price: v.number(),
  booking_transaction_id: v.optional(v.nullable(v.string())),
  tax: v.number(),
  applied_coupon: v.optional(v.nullable(v.string())),
  from_date_time: v.optional(v.nullable(v.number())),
  to_date_time: v.optional(v.nullable(v.number())),
  number_of_rooms: v.number(),
  origin: v.optional(v.nullable(v.string())),
  destination: v.string(),
  travel_class: v.optional(v.nullable(v.string())),
  trip_type: v.optional(v.nullable(v.string())),
  booking_id: v.string(),
  booking_parent_id: v.optional(v.nullable(v.string())),
  booking_date: v.string(),
  is_self_booking: v.boolean(),
  payment_type: v.optional(v.nullable(v.string())),
  price_detail: v.optional(
    v.nullable(
      v.object({
        total_discount_amount: v.optional(v.nullable(v.number())),
        cost_amount: v.optional(v.nullable(v.number())),
        sell_amount: v.optional(v.nullable(v.number())),
        gr_amount: v.optional(v.nullable(v.number())),
        sp_amount: v.optional(v.nullable(v.number())),
        gmv_amount: v.optional(v.nullable(v.number()))
      })
    )
  )
});

// Event Detail Schema
export const EventDetailCabs = v.object({
  event_name: v.string(),
  event_type: v.string(),
  event_value: v.optional(v.nullable(v.string())),
  booking_info: v.optional(v.nullable(BookingInfoCabs)),
  components: v.optional(v.nullable(ComponentsCabs)),
  event_id: v.optional(v.nullable(v.string())),
  event_timestamp: v.optional(v.nullable(v.number()))
});

// Locus Schema
export const LocusCabs = v.object({
  locus_id: v.string(),
  locus_type: v.string(),
  country: v.string(),
  lat: v.optional(v.nullable(v.number())),
  long: v.optional(v.nullable(v.number())),
  locus_v2_id: v.optional(v.nullable(v.string())),
  locus_v2_type: v.optional(v.nullable(v.string()))
});

// Location Schema
export const LocationCabs = v.object({
  code: v.string(),
  name: v.string(),
  type: v.optional(v.nullable(v.string())),
  country_code: v.string(),
  country: v.string()
});

// Pax Details Schema
export const PaxDetailsCabs = v.object({
  child: v.optional(
    v.nullable(
      v.object({
        count: v.number(),
        ages: v.array(v.optional(v.nullable(v.string())))
      })
    )
  ),
  adult: v.object({
    count: v.number()
  }),
  infants: v.optional(
    v.nullable(
      v.object({
        count: v.number()
      })
    )
  )
});

// Pax Schema
export const PaxCabs = v.object({
  count: v.number(),
  details: PaxDetailsCabs,
  rooms: v.optional(v.nullable(v.number()))
});

// Search Context Schema
export const SearchContextCabs = v.object({
  travel_purpose_opted: v.optional(v.nullable(v.boolean())),
  checkin_time: v.optional(v.nullable(v.string())),
  checkin_duration: v.optional(v.nullable(v.string())),
  to: v.optional(
    v.nullable(
      v.object({
        locus: LocusCabs,
        location: v.optional(v.nullable(LocationCabs))
      })
    )
  ),
  from: v.optional(
    v.nullable(
      v.object({
        locus: LocusCabs,
        location: v.optional(v.nullable(LocationCabs))
      })
    )
  ),
  to_date_time: v.optional(v.nullable(v.number())),
  pax: v.optional(v.nullable(PaxCabs)),
  from_date_time: v.optional(v.nullable(v.number())),
  product_id: v.optional(v.nullable(v.string())),
  search_text: v.optional(v.nullable(v.string())),
  search_type: v.optional(v.nullable(v.string())),
  trip_type: v.optional(v.nullable(v.string())),
  journey_type: v.optional(v.nullable(v.string())),
  special_fares: v.optional(v.nullable(v.string())),
  travel_class: v.string(),
  stops: v.optional(
    v.nullable(
      v.array(
        v.object({
          index: v.number(),
          locus: LocusCabs,
          location: v.optional(v.nullable(LocationCabs))
        })
      )
    )
  ),
  tags: v.optional(
    v.nullable(
      v.array(
        v.object({
          type: v.string(),
          value: v.string()
        })
      )
    )
  ),
  advance_purchase: v.optional(v.nullable(v.string())),
  product_id_list: v.optional(v.nullable(v.array(v.string()))),
  visa_type: v.optional(v.nullable(v.string()))
});

// Traveller Info Schema
export const TravellerInfoItemCabs = v.object({
  is_primary: v.boolean(),
  m_cty_cd: v.optional(v.nullable(v.string())),
  email_id: v.optional(v.nullable(v.string())),
  fname: v.string(),
  lname: v.string(),
  title: v.string(),
  gst_optd: v.boolean(),
  moblie_com_id: v.optional(v.nullable(v.string())),
  email_com_id: v.optional(v.nullable(v.string())),
  mob_id: v.string(),
  passport_details: v.optional(
    v.nullable(
      v.object({
        passport_expiry_date: v.string(),
        passport_issue_place: v.string(),
        passport_number: v.string()
      })
    )
  ),
  date_of_birth: v.optional(v.nullable(v.string())),
  age: v.optional(v.nullable(v.string())),
  gender: v.string(),
  gst_details: v.optional(
    v.nullable(
      v.object({
        company_name: v.string(),
        company_address: v.string(),
        gst_no: v.string()
      })
    )
  ),
  type: v.optional(v.nullable(v.string())),
  nationality: v.optional(v.nullable(v.string()))
});

// Adobe Analytics Schema
export const AdobeAnalyticsCabs = v.object({
  error_details_v11: v.optional(v.nullable(v.string())),
  hydra_segments_l2: v.optional(v.nullable(v.string())),
  components_l1: v.optional(v.nullable(v.string()))
});

// Entity Info Schema
export const EntityInfoCabs = v.object({
  region: v.string(),
  currency: v.string(),
  entity: v.string(),
  organisation: v.string(),
  user_currency: v.optional(v.nullable(v.string())),
  user_country: v.optional(v.nullable(v.string())),
  user_language: v.string()
});

// Organization Context Schema
export const OrganizationContextCabs = v.object({
  org_id: v.optional(v.nullable(v.string())),
  org_type: v.optional(v.nullable(v.string())),
  gst_verified: v.optional(v.nullable(v.string())),
  gst_turnover: v.optional(v.nullable(v.string())),
  size: v.optional(v.nullable(v.string())),
  state: v.optional(v.nullable(v.string())),
  activation_date: v.optional(v.nullable(v.string())),
  domain_type: v.optional(v.nullable(v.string())),
  verification_status: v.optional(v.nullable(v.string()))
});

// Identities Schema
export const IdentitiesCabs = v.object({
  ECID: v.optional(v.nullable(v.string())),
  phone_number_hash: v.optional(v.nullable(v.string())),
  email_address_hash: v.optional(v.nullable(v.string()))
});

// Campaign Details Schema
export const CampaignDetailsCabs = v.object({
  campaign_name: v.string(),
  campaign_key: v.string()
});

// Tracking Info Schema
export const TrackingInfoCabs = v.object({
  sponsored_data: v.array(
    v.optional(
      v.nullable(
        v.object({
          type: v.optional(v.nullable(v.string())),
          position: PositionCabs,
          tracking: v.object({
            lob: v.optional(v.nullable(v.string())),
            tracking_id: v.string(),
            section: v.optional(v.nullable(v.string())),
            advertiser: v.object({
              advertisement_id: v.string(),
              campaign_id: v.string(),
              advertiser_id: v.string()
            }),
            product_id: v.optional(v.nullable(v.string()))
          })
        })
      )
    )
  )
});

// Addon Details Schema
export const AddonDetailsItemCabs = v.object({
  addontype: v.string(),
  id: v.string(),
  currency: v.string(),
  includedunits: v.number(),
  unitprice: v.number(),
  unittype: v.string(),
  heading: v.string(),
  selected: v.boolean(),
  available: v.boolean(),
  is_preselected: v.boolean(),
  addon_displayed_benefits: v.optional(v.nullable(v.array(v.string()))),
  addon_image: v.optional(v.nullable(v.string())),
  meta: v.optional(v.nullable(v.string()))
});

// Main Base Schema for Cabs using extendSchema pattern
export const BaseSchemaCabs = v.object({
  page_context: PageContextCabs,
  event_tracking_context: EventTrackingContextCabs,
  error_details_list: v.optional(v.nullable(v.array(ErrorDetailsItemCabs))),
  user_context: v.optional(UserContextCabs),
  device_context: v.optional(DeviceContextCabs),
  experiment_details: v.optional(v.nullable(ExperimentDetailsCabs)),
  campaign_details: v.optional(v.nullable(CampaignDetailsCabs)),
  tracking_info: v.optional(v.nullable(TrackingInfoCabs)),
  event_detail: EventDetailCabs,
  traveller_info: v.optional(v.nullable(v.array(TravellerInfoItemCabs))),
  addon_details: v.optional(v.nullable(v.array(AddonDetailsItemCabs))),
  search_context: SearchContextCabs,
  entity_info: v.optional(v.nullable(EntityInfoCabs)),
  organization_context: v.optional(v.nullable(OrganizationContextCabs)),
  identities: v.optional(v.nullable(IdentitiesCabs)),
  adobe_analytics: v.optional(v.nullable(AdobeAnalyticsCabs))
});

// Export type inferred from main schema (used in fireEventHelper.ts)
export type BaseCabsEvent = v.InferInput<typeof BaseSchemaCabs>;
